import { createPortal } from 'react-dom';

import { useFloatingToolbarHost } from './FloatingToolbarHost';

import type { ReactNode } from 'react';

/**
 * Clicks inside an element carrying this attribute are UI over the canvas,
 * not a click away from the selection: a surface that deselects on an
 * outside click skips them (PLAT-12102 DS6). The toolbar layer carries it, and
 * so do the pickers a bar opens in their own portals (ColorPicker, the
 * captions font menu).
 */
export const KEEPS_CANVAS_SELECTION_ATTR = 'data-keeps-canvas-selection';

/** True for a click on toolbar UI (see `KEEPS_CANVAS_SELECTION_ATTR`). */
export const keepsCanvasSelection = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  !!target.closest(`[${KEEPS_CANVAS_SELECTION_ATTR}]`);

/**
 * Where a bar and its menus render (PLAT-12102 DS2). With no `host.layer`
 * (Creator Studio) they render in place, inside the stage's stacking context,
 * exactly as before. With one (the clip preview) they move to a body-level
 * layer at that z-index, above the canvas iframe and outside the modal's
 * transformed, clipped stage — the bars and menus are `position: fixed`, so
 * they keep their viewport coordinates.
 */
export const ToolbarLayer = ({ children }: { children: ReactNode }) => {
  const layer = useFloatingToolbarHost()?.layer;
  if (!layer || typeof document === 'undefined') return <>{children}</>;
  return createPortal(
    <div
      {...{ [KEEPS_CANVAS_SELECTION_ATTR]: '' }}
      data-testid="floating-toolbar-layer"
      style={{ position: 'relative', zIndex: layer.zIndex }}
    >
      {children}
    </div>,
    document.body,
  );
};
